import React from "react";

import {
  usePutSegmentFindFilterQuery,
  usePutSegmentMoveQuery,
} from "@tiny/api";
import useSearchable from "@tiny/components/src/hooks/useSearchable";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { showUploadMessage } from "@tiny/components/src/Snackbar/snackbar-helpers";
import { MoveToAudienceDialogProps } from "./MoveToAudienceDialog.types";

const pageableDefault = {
  page: 0,
  size: 5,
  sort: ["updatedAt,DESC"],
};

export function useMoveToAudienceDialog(props: MoveToAudienceDialogProps) {
  const { sourceSegmentId, selectedIds, moveAll, onClose, onSuccess } = props;

  const [targetId, setTargetId] = React.useState<string>();

  const {
    pageable,
    search,
    searchDebounced,
    handleSearchChange,
    handlePageChange,
    handleSizeChange,
  } = useSearchable(pageableDefault, "moveToAudience");

  const { data: { content = [], totalElements = 0 } = {}, isLoading } =
    usePutSegmentFindFilterQuery(
      {
        searchTextName: searchDebounced,
        segmentTypes: ["UPLOAD"],
      },
      pageable,
      {
        keepPreviousData: true,
      }
    );

  const audiences = React.useMemo(
    () => content.filter((a) => a.id !== sourceSegmentId),
    [content, sourceSegmentId]
  );

  const { mutateAsync: moveContacts, isLoading: isMoving } =
    usePutSegmentMoveQuery();

  const handleSelect = React.useCallback((id?: string) => {
    setTargetId(id);
  }, []);

  const handleClose = React.useCallback(() => {
    setTargetId(undefined);
    onClose();
  }, [onClose]);

  const handleConfirm = React.useCallback(async () => {
    if (!sourceSegmentId || !targetId) return;
    try {
      const result = await moveContacts({
        data: {
          sourceAudienceId: sourceSegmentId,
          targetAudienceId: targetId,
          contactIds: moveAll ? [] : selectedIds,
          moveAll,
        },
      });
      showUploadMessage({
        variant: "success",
        message: "Moved successfully!",
        description: `${result.movedCount} contacts successfully moved`,
        showClose: true,
      });
      setTargetId(undefined);
      onSuccess();
      onClose();
    } catch (e: any) {
      const be = await extractBackendError(e.response);
      showUploadMessage({
        variant: "failed",
        message: "Failed to move contacts",
        description: getErrorMessage(be),
        showClose: true,
      });
    }
  }, [
    sourceSegmentId,
    targetId,
    moveContacts,
    moveAll,
    selectedIds,
    onSuccess,
    onClose,
  ]);

  return {
    audiences,
    isLoading,
    isMoving,
    search,
    handleSearchChange,
    page: pageable.page,
    itemsPerPage: pageable.size,
    itemsPerPageOptions: [5, 10, 15, 20],
    totalCount: totalElements,
    handlePageChange,
    handleSizeChange,
    targetId,
    handleSelect,
    handleConfirm,
    handleClose,
  };
}
